import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getCustomerEnquiries } from '../../api/enquiries';
import { FaArrowLeft, FaCalendarAlt, FaUsers, FaMoneyBillWave, FaTag } from 'react-icons/fa';
import { MdHotel, MdLocationCity } from 'react-icons/md';
import moment from 'moment';

const STATUS_STYLES = {
  new:        'bg-blue-50 text-blue-700 border-blue-200',
  open:       'bg-amber-50 text-amber-700 border-amber-200',
  successful: 'bg-green-50 text-green-700 border-green-200',
  lost:       'bg-red-50 text-red-700 border-red-200',
};

function StatusBadge({ status }) {
  const style = STATUS_STYLES[status?.toLowerCase()] || 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border capitalize ${style}`}>
      {status || 'Unknown'}
    </span>
  );
}

function EnquiryCard({ enquiry, type }) {
  const image =
    type === 'venue'
      ? enquiry?.venueId?.images?.[0] || enquiry?.venueImage || null
      : enquiry?.hotelId?.images?.[0] || enquiry?.hotelImage || null;

  const placeName =
    type === 'venue'
      ? enquiry?.venueName || enquiry?.venueId?.venueName
      : enquiry?.hotelName || enquiry?.hotelId?.hotelName;

  const location =
    type === 'venue'
      ? enquiry?.venueId?.city || enquiry?.venueId?.location
      : enquiry?.hotelId?.city || enquiry?.hotelId?.location;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className="flex h-36">
        {/* Image */}
        <div className="w-44 shrink-0 bg-gray-100">
          {image ? (
            <img src={image} alt={placeName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 gap-2">
              {type === 'venue' ? (
                <MdLocationCity className="text-5xl text-blue-300" />
              ) : (
                <MdHotel className="text-5xl text-blue-300" />
              )}
              <span className="text-xs text-blue-300 font-medium">No Image</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 px-4 py-3 flex flex-col justify-between overflow-hidden">
          <div className="flex justify-between items-start gap-3 mb-2">
            <div>
              <h3 className="text-sm font-bold text-gray-800 leading-tight line-clamp-1">
                {placeName || '--'}
              </h3>
              {location && (
                <p className="text-xs text-gray-400 mt-0.5">{location}</p>
              )}
            </div>
            <StatusBadge status={enquiry?.status} />
          </div>

          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <FaCalendarAlt className="text-blue-400 shrink-0" />
              <div>
                <span className="block text-gray-400 text-[10px] uppercase tracking-wide leading-none mb-0.5">Event Date</span>
                <span className="font-medium text-gray-700">
                  {enquiry?.startDate ? moment(enquiry.startDate).format('DD MMM YYYY') : '--'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <FaUsers className="text-indigo-400 shrink-0" />
              <div>
                <span className="block text-gray-400 text-[10px] uppercase tracking-wide leading-none mb-0.5">Guests</span>
                <span className="font-medium text-gray-700">{enquiry?.guests || '--'}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <FaMoneyBillWave className="text-green-400 shrink-0" />
              <div>
                <span className="block text-gray-400 text-[10px] uppercase tracking-wide leading-none mb-0.5">Budget</span>
                <span className="font-medium text-gray-700">
                  {enquiry?.budget ? `AED ${enquiry.budget}` : '--'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <FaTag className="text-rose-400 shrink-0" />
              <div>
                <span className="block text-gray-400 text-[10px] uppercase tracking-wide leading-none mb-0.5">Event Type</span>
                <span className="font-medium text-gray-700 capitalize">{enquiry?.eventType || '--'}</span>
              </div>
            </div>
          </div>

          <p className="text-[11px] text-gray-400 border-t border-gray-100 pt-1.5 mt-1">
            Submitted {enquiry?.createdAt ? moment(enquiry.createdAt).fromNow() : '--'}
          </p>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ type }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        {type === 'venue' ? (
          <MdLocationCity className="text-3xl text-gray-300" />
        ) : (
          <MdHotel className="text-3xl text-gray-300" />
        )}
      </div>
      <p className="text-gray-400 font-medium">No {type} enquiries</p>
      <p className="text-gray-300 text-sm mt-1">This customer has not made any {type} enquiries yet.</p>
    </div>
  );
}

export default function CustomerEnquiries() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const customerName = location.state?.customerName;

  const [activeTab, setActiveTab] = useState('venues');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['customerEnquiries', customerId],
    queryFn: () => getCustomerEnquiries(customerId),
    refetchOnWindowFocus: false,
  });

  const venueEnquiries = data?.enquiries?.venue || [];
  const hotelEnquiries = data?.enquiries?.hotel || [];
  const counts = data?.count || { venue: 0, hotel: 0, total: 0 };

  const tabs = [
    { key: 'venues', label: 'Venues', count: counts.venue },
    { key: 'hotels', label: 'Hotels', count: counts.hotel },
  ];

  const activeList = activeTab === 'venues' ? venueEnquiries : hotelEnquiries;
  const activeType  = activeTab === 'venues' ? 'venue' : 'hotel';

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-4xl mx-auto">

        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium mb-5"
        >
          <FaArrowLeft /> Back to Customers
        </button>

        {/* Page header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {customerName ? `${customerName}'s Enquiries` : 'Customer Enquiries'}
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              All venue and hotel enquiries submitted by this customer
            </p>
          </div>

          {/* Summary pill */}
          {!isLoading && (
            <div className="flex gap-3">
              <div className="bg-white border border-gray-200 rounded-xl px-4 py-2 text-center shadow-sm">
                <span className="text-xl font-bold text-blue-600">{counts.venue}</span>
                <p className="text-[11px] text-gray-400 mt-0.5">Venues</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl px-4 py-2 text-center shadow-sm">
                <span className="text-xl font-bold text-indigo-600">{counts.hotel}</span>
                <p className="text-[11px] text-gray-400 mt-0.5">Hotels</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl px-4 py-2 text-center shadow-sm">
                <span className="text-xl font-bold text-gray-700">{counts.total}</span>
                <p className="text-[11px] text-gray-400 mt-0.5">Total</p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6 bg-white rounded-t-xl px-2">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-6 py-3.5 text-sm font-medium transition-colors relative ${
                activeTab === tab.key
                  ? 'text-[#FE4747]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {activeTab === tab.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FE4747] rounded-t" />
              )}
              {tab.label}
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
                activeTab === tab.key
                  ? 'bg-red-50 text-[#FE4747]'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm text-gray-400">Loading enquiries…</span>
          </div>
        ) : isError ? (
          <div className="text-center py-20">
            <p className="text-red-400 font-medium">Failed to load enquiries.</p>
            <p className="text-gray-400 text-sm mt-1">Please try again later.</p>
          </div>
        ) : activeList.length === 0 ? (
          <EmptyState type={activeType} />
        ) : (
          <div className="space-y-4">
            {activeList.map((enquiry, index) => (
              <EnquiryCard key={enquiry._id || index} enquiry={enquiry} type={activeType} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
